// src/services/pushNotificationService.js
// Updated: 01/12/2026

import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { db } from "./firebase";
import { doc, setDoc, deleteDoc, serverTimestamp, getDoc } from "firebase/firestore";


// Public VAPID key (Web Push)
const VAPID_KEY =
  "BDuwR94C7c6Bp0J9C67Ds7y3FR6tG7HNKm4cJ5THIqMLK-5n2Zv-gqEFAmtwLZdgPFcJ_tVYgJ-c5a2Y1Z-cz9c";

// Local cache keys
const TOKEN_STORAGE_KEY = "fcm_token_v1";
const TOKEN_LAST_SYNC_KEY = "fcm_token_last_sync";

// 24 Hour Timeout (in milliseconds)
const SYNC_THRESHOLD = 24 * 60 * 60 * 1000;

const isRunningInPWA = () => {
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  // eslint-disable-next-line no-undef
  if (window.navigator && window.navigator.standalone) {
    return true;
  }
  return false;
};

const ensureMessagingServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    try {
      await registration.update();
    } catch (e) {
      // ignore
    }
    return registration;
  } catch (error) {
    console.error("Failed to register firebase-messaging-sw.js:", error);
    return null;
  }
};

/**
 * Registers (or refreshes) the FCM token.
 * Optimized for cost:
 * 1. Takes 'churchSettings' argument to avoid fetching it from DB (saves 1 read).
 * 2. Checks localStorage. If token is unchanged and synced < 24h ago, skips DB write (saves 1 write).
 */
export const initializeFCM = async (user, userProfile, churchSettings) => {
  if (!user || !userProfile?.churchId) return;

  try {
    // 1. Check settings passed from Context (No DB Read)
    if (!churchSettings || !churchSettings.enablePushNotifications) {
      // Optionally log this once, but it's normal behavior
      return;
    }

    if (!("Notification" in window)) {
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return;
    }

    const registration = await ensureMessagingServiceWorker();
    if (!registration) {
      return;
    }

    const messaging = getMessaging();

    // Get the current token from Firebase (Cached locally by SDK usually)
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      return;
    }

    // --- COST OPTIMIZATION LOGIC ---
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const lastSyncStr = localStorage.getItem(TOKEN_LAST_SYNC_KEY);
    const lastSync = lastSyncStr ? parseInt(lastSyncStr, 10) : 0;
    const now = Date.now();

    const isTokenChanged = storedToken !== currentToken;
    const isStale = (now - lastSync) > SYNC_THRESHOLD;

    // If the token is exactly the same as before, AND we synced it recently, do nothing.
    // This makes the "visibility" check free (0 reads, 0 writes).
// If token looks unchanged and recently synced, still verify Firestore still has it.
// This fixes the case where the server deleted the token doc but the browser still has the same token.
if (!isTokenChanged && !isStale) {
  const tokenRef = doc(db, `users/${user.uid}/fcmTokens/${currentToken}`);
  const tokenSnap = await getDoc(tokenRef);

  if (tokenSnap.exists()) {
    console.log("FCM Token is valid, fresh, and still registered. Skipping DB write.");
    return;
  }

  console.warn("FCM token doc missing in Firestore. Re-registering token...");
  // Fall through and re-save token doc below
}

    // -------------------------------

    // If token rotated, delete old token doc
    if (isTokenChanged && storedToken) {
      try {
        await deleteDoc(doc(db, `users/${user.uid}/fcmTokens/${storedToken}`));
      } catch (e) {
        console.warn("Failed to delete old token doc:", e);
      }
    }

    // Upsert the new token doc
    const tokenRef = doc(db, `users/${user.uid}/fcmTokens/${currentToken}`);
    await setDoc(
      tokenRef,
      {
        token: currentToken,
        userId: user.uid,
        churchId: userProfile.churchId,
        updatedAt: serverTimestamp(),
        ua: navigator.userAgent,
        isPwa: isRunningInPWA(),
        swScope: registration.scope || null,
      },
      { merge: true }
    );

    // Update local cache
    localStorage.setItem(TOKEN_STORAGE_KEY, currentToken);
    localStorage.setItem(TOKEN_LAST_SYNC_KEY, now.toString());
    console.log("FCM Token synced to Firestore.");

    // Foreground listener
    onMessage(messaging, (payload) => {
      console.log("Message received in foreground:", payload);
    });

  } catch (error) {
    console.error("An error occurred while initializing FCM.", error);
  }
};